import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface SystemSettings {
    show_watch_ads: boolean;
    show_creatives: boolean;
    show_boost: boolean;
    show_venue_ads: boolean;
    show_partner_program: boolean;
    maintenance_mode: boolean;
    landing_maintenance_mode: boolean;
    maintenance_return_time?: string;
}

export const useSystemSettings = () => {
    const [settings, setSettings] = useState<SystemSettings>({
        show_watch_ads: true,
        show_creatives: true,
        show_boost: true,
        show_venue_ads: true,
        show_partner_program: true,
        maintenance_mode: false,
        landing_maintenance_mode: false,
        maintenance_return_time: undefined
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let channel: any;

        const fetchData = async () => {
            try {
                const { data, error } = await supabase
                    .from('system_settings')
                    .select('*')
                    .limit(1)
                    .single();

                if (data) {
                    setSettings({
                        show_watch_ads: data.show_watch_ads ?? true,
                        show_creatives: data.show_creatives ?? true,
                        show_boost: data.show_boost ?? true,
                        show_venue_ads: data.show_venue_ads ?? true,
                        show_partner_program: data.show_partner_program ?? true,
                        maintenance_mode: data.maintenance_mode ?? false,
                        landing_maintenance_mode: data.landing_maintenance_mode ?? false,
                        maintenance_return_time: data.maintenance_return_time
                    });
                }
            } catch (e) {
                console.error("Erro ao carregar settings:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Realtime updates para refletir na hora. Usamos ID único para evitar colisão de canais.
        const channelId = `system_settings_${Math.random().toString(36).substring(7)}`;
        channel = supabase
            .channel(channelId)
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'system_settings' },
                (payload: any) => {
                    console.log('🔄 [SystemSettings] Realtime update received:', payload);
                    const newData = payload.new;
                    if (newData) {
                        setSettings(prev => ({
                            ...prev,
                            show_watch_ads: newData.show_watch_ads ?? prev.show_watch_ads,
                            show_creatives: newData.show_creatives ?? prev.show_creatives,
                            show_boost: newData.show_boost ?? prev.show_boost,
                            show_venue_ads: newData.show_venue_ads ?? prev.show_venue_ads,
                            show_partner_program: newData.show_partner_program ?? prev.show_partner_program,
                            maintenance_mode: newData.maintenance_mode ?? prev.maintenance_mode,
                            landing_maintenance_mode: newData.landing_maintenance_mode ?? prev.landing_maintenance_mode,
                            maintenance_return_time: newData.maintenance_return_time ?? prev.maintenance_return_time
                        }));
                    }
                }
            )
            .subscribe();

        // Polling de fallback a cada 5 segundos para garantir atualização mesmo se o socket falhar
        const intervalId = setInterval(() => {
            fetchData();
        }, 5000);

        return () => {
            if (channel) supabase.removeChannel(channel);
            clearInterval(intervalId);
        };
    }, []);

    return { settings, loading };
};
